// Full schema for the Plantagenet Hall Technical Site Survey Workbook.
// Block types: note | fields | checklist | wmc | table | measurements | photoRegister | textarea

export const UNITS = ["mm", "cm", "m", "ft", "in", "seats", "deg", "kg", "lux", "dB", "kW", "A", "V"];

export const META_FIELDS = [
  { k: "survey_date", label: "Survey Date", type: "date" },
  { k: "survey_lead", label: "Survey Lead", type: "text" },
  { k: "venue_rep", label: "Venue Representative", type: "text" },
  { k: "other_attendees", label: "Other Attendees", type: "text" },
  { k: "start_time", label: "Start Time", type: "time" },
  { k: "finish_time", label: "Finish Time", type: "time" },
  { k: "revision", label: "Revision / File Reference", type: "text" },
];

const STATUS_OPTS = ["OK", "Issue", "Maintenance", "Do not use"];
const YN = ["Yes", "No", "N/A"];

export const PARTS = [
  { id: "A", title: "Prepare", color: "#3B82F6" },
  { id: "B", title: "Stage & Lighting", color: "#F59E0B" },
  { id: "C", title: "Control & Firmware", color: "#8B5CF6" },
  { id: "D", title: "Systems & Infrastructure", color: "#10B981" },
  { id: "E", title: "Test & Close-out", color: "#EF4444" },
  { id: "F", title: "Reference", color: "#64748B" },
];

export const SECTIONS = [
  // ---------------- PART A ----------------
  {
    id: "1", part: "A", num: "1", title: "Survey purpose & completion standard",
    blocks: [
      { type: "note", text: "Create a permanent, show-independent technical reference for Plantagenet District Hall. Record what is there — before touching anything." },
      { type: "checklist", key: "completion", title: "Completion standard", items: [
        { k: "photograph_all", label: "Photograph stage, auditorium, bio box, backstage, technical infrastructure and stored equipment" },
        { k: "measure_all", label: "Measure stage, proscenium, wings, apron, auditorium, lighting positions, projector position and screen" },
        { k: "inventory_all", label: "Inventory every installed and spare fixture — make/model, mode, DMX address, status" },
        { k: "confirm_bars", label: "Confirm actual number and location of FOH and stage LX bars/trusses — no assumptions" },
        { k: "export_show", label: "Export and preserve existing Mantra show before any changes" },
        { k: "trace_audio", label: "Trace and document complete audio system, stage input to loudspeaker" },
        { k: "test_projector", label: "Identify and fully test projector, screen and signal path" },
        { k: "document_infra", label: "Document DMX, network, power, comms, house/work lights, haze/effects, curtains, access & safety" },
        { k: "e2e_test", label: "Run a complete end-to-end system test and record all faults/actions" },
        { k: "collect_generic", label: "Collect everything needed for a generic Venue Technical Reference" },
      ] },
      { type: "textarea", key: "assumptions", label: "Planning assumptions to verify" },
    ],
  },
  {
    id: "2", part: "A", num: "2", title: "Things to take with you",
    blocks: [
      { type: "checklist", key: "measuring_tools", title: "A. Measuring & stage survey tools", items: [
        { k: "laser", label: "Laser distance meter + spare/charged battery" },
        { k: "tape_short", label: "5–8 m tape measure" },
        { k: "tape_long", label: "20–30 m long tape (auditorium/stage depth, projector throw)" },
        { k: "rule", label: "Steel ruler / 1 m rule" },
        { k: "level", label: "Spirit level or digital angle gauge" },
        { k: "chalk", label: "Chalk / removable marking tape" },
        { k: "graph", label: "Graph paper, clipboard, printed workbook" },
        { k: "pens", label: "Pencils, pens, permanent & fine-tip markers" },
        { k: "calc", label: "Calculator or phone calculator" },
        { k: "torch", label: "Head torch / torch" },
      ] },
      { type: "checklist", key: "photo_tools", title: "B. Photo & documentation tools", items: [
        { k: "camera", label: "Phone/camera — free storage, fully charged" },
        { k: "powerbank", label: "Power bank + charging cable" },
        { k: "wide", label: "Wide-angle capability if available" },
        { k: "tripod", label: "Small tripod or monopod (optional)" },
        { k: "slate", label: "Photo ID slate / whiteboard / paper labels" },
        { k: "cloth", label: "Microfibre cloth for labels and screens" },
        { k: "backup_folder", label: "Cloud or laptop folder ready for photo backup" },
      ] },
      { type: "checklist", key: "usb", title: "C. USB drives (take at least three, labelled)", items: [
        { k: "usb_a", label: "USB A — existing show backup (use first)" },
        { k: "usb_b", label: "USB B — second backup copy (kept separate)" },
        { k: "usb_c", label: "USB C — LSC update / fixture library" },
        { k: "usb_d", label: "USB D — spare (optional)" },
        { k: "labelled", label: "Every USB labelled so update drive can't be confused with backup" },
      ] },
      { type: "checklist", key: "laptop", title: "D. Laptop, software & files", items: [
        { k: "laptop", label: "Laptop with charger" },
        { k: "editor", label: "LSC Mantra Editor installed and tested" },
        { k: "software_file", label: "Official current Mantra Lite software file" },
        { k: "lib_file", label: "Official current fixture library" },
        { k: "legacy", label: "Legacy v3.07a package (only if console requires)" },
        { k: "docs", label: "Copies of current venue/show technical documents" },
        { k: "template", label: "Inventory spreadsheet/template" },
        { k: "qlab", label: "QLab Mac if show-control path is tested" },
        { k: "sl_control", label: "StudioLive control / Universal Control software" },
      ] },
      { type: "checklist", key: "adapters", title: "E. Data, AV & adapters", items: [
        { k: "eth", label: "Known-good Ethernet leads (short + long)" },
        { k: "usb_adapt", label: "USB-A / USB-C adapters / hub" },
        { k: "hdmi", label: "Known-good HDMI lead + adapters" },
        { k: "xlr", label: "Known-good balanced XLR mic lead(s)" },
        { k: "mic", label: "Known-good microphone" },
        { k: "audio_if", label: "3.5 mm / USB audio interface or DI for playback" },
        { k: "dmx", label: "DMX 5-pin lead(s) + 3↔5 pin adapters" },
        { k: "term", label: "DMX terminator" },
        { k: "switch", label: "Network switch (temporary isolated test only)" },
      ] },
      { type: "checklist", key: "safety", title: "F. Test equipment, safety & access", items: [
        { k: "ppe", label: "PPE required by venue" },
        { k: "footwear", label: "Closed footwear" },
        { k: "gloves", label: "Gloves for dusty stored equipment" },
        { k: "ladder", label: "Approved ladder/EWP access arranged through venue" },
        { k: "elec_test", label: "Electrical test equipment (only if licensed/authorised)" },
        { k: "cable_test", label: "Small cable tester for XLR/DMX/Ethernet" },
        { k: "gaffer", label: "Gaffer + removable painter tape" },
        { k: "water", label: "Drinking water and enough time" },
      ] },
      { type: "checklist", key: "before_leaving", title: "G. Before leaving home", items: [
        { k: "charge", label: "Charge phone/camera, laptop, laser meter, power bank" },
        { k: "storage", label: "Check free storage on phone/camera and laptop" },
        { k: "folder", label: "Create photo folder and naming convention" },
        { k: "download", label: "Download official LSC files while internet is reliable" },
        { k: "access", label: "Confirm venue access, keys, alarm, authorisations" },
      ] },
      { type: "fields", key: "previsit", title: "Pre-visit notes", fields: [
        { k: "contacts", label: "Contacts", type: "text", full: true },
        { k: "access_times", label: "Access times", type: "text" },
        { k: "keys", label: "Keys", type: "text" },
        { k: "authorisations", label: "Authorisations", type: "text" },
      ] },
    ],
  },
  // ---------------- PART B ----------------
  {
    id: "3", part: "B", num: "3", title: "Photographic survey",
    blocks: [
      { type: "note", text: "For each important item take three shots: W wide context, M medium installation, C close-up of model/label/connections. Number photos PH-001 onward." },
      { type: "wmc", key: "photo_plan", title: "Stage & auditorium", items: [
        { k: "stage_rear", label: "Stage from rear auditorium (centre/left/right)" },
        { k: "stage_mid", label: "Stage from mid auditorium" },
        { k: "stage_front", label: "Stage from front row centre" },
        { k: "into_aud", label: "Stage looking into auditorium" },
        { k: "prosc", label: "Full proscenium" },
        { k: "apron", label: "Apron and stage floor" },
        { k: "upstage", label: "Upstage / rear stage" },
        { k: "sl_wing", label: "Stage Left wing" },
        { k: "sr_wing", label: "Stage Right wing" },
        { k: "crossover", label: "Backstage crossover" },
      ] },
      { type: "wmc", key: "photo_light", title: "Lighting & projection", items: [
        { k: "foh", label: "FOH lighting positions" },
        { k: "lx_bars", label: "Each stage LX bar/truss" },
        { k: "side", label: "Each side-lighting position/truss" },
        { k: "screen", label: "Projection screen fully deployed" },
        { k: "projector", label: "Projector location / mount / lens" },
        { k: "effects", label: "Haze / mirror ball / motor / pinspots" },
        { k: "house_ctrl", label: "House/work light controls" },
      ] },
      { type: "wmc", key: "photo_control", title: "Control & audio", items: [
        { k: "biobox", label: "Bio box / control booth overview" },
        { k: "mantra", label: "Mantra + both Wings" },
        { k: "studiolive", label: "StudioLive console" },
        { k: "racks", label: "Audio racks / stagebox / amps / DSP" },
        { k: "speakers", label: "Each installed speaker" },
      ] },
      { type: "wmc", key: "photo_infra", title: "Infrastructure & access", items: [
        { k: "dmx", label: "DMX outlets / splitters / patch panels" },
        { k: "net", label: "Network switches / outlets" },
        { k: "power", label: "Technical power outlets / distribution" },
        { k: "curtains", label: "Curtains / tracks / masking" },
        { k: "loading", label: "Loading access and stage doors" },
        { k: "storage", label: "Storage / spare equipment" },
        { k: "labels", label: "Labels, serial plates, safety/load markings" },
      ] },
    ],
  },
  {
    id: "4", part: "B", num: "4", title: "Photo register",
    blocks: [
      { type: "note", text: "Capture photos on-site. Each photo is auto-numbered PH-001 onward. Tag with area/item, view (W/M/C) and notes so every item traces back to a clear image." },
      { type: "photoRegister" },
    ],
  },
  {
    id: "5", part: "B", num: "5", title: "Stage, proscenium & auditorium measurements",
    blocks: [
      { type: "note", text: "Record in millimetres where practical. Photograph the tape/laser readout for critical dimensions." },
      { type: "measurements", key: "stage_dims", rows: [
        { k: "A", label: "A — Proscenium opening width", unit: "mm" },
        { k: "B", label: "B — Proscenium opening height", unit: "mm" },
        { k: "C", label: "C — Stage width (wall to wall)", unit: "mm" },
        { k: "D", label: "D — Stage depth (setting line to back wall)", unit: "mm" },
        { k: "E", label: "E — Apron depth (setting line to edge)", unit: "mm" },
        { k: "F", label: "F — Stage height above auditorium floor", unit: "mm" },
        { k: "G", label: "G — SL wing width", unit: "mm" },
        { k: "H", label: "H — SR wing width", unit: "mm" },
        { k: "I", label: "I — Crossover width", unit: "mm" },
        { k: "J", label: "J — Grid / ceiling height above stage", unit: "mm" },
        { k: "K", label: "K — Auditorium width", unit: "mm" },
        { k: "L", label: "L — Auditorium depth (stage to rear wall)", unit: "mm" },
        { k: "M", label: "M — Number of seats", unit: "seats" },
        { k: "N", label: "N — Centre aisle width", unit: "mm" },
        { k: "O", label: "O — Front row to setting line", unit: "mm" },
      ] },
    ],
  },
  {
    id: "6", part: "B", num: "6", title: "Stage floor-plan field sketch",
    blocks: [
      { type: "note", text: "Mark CL, plaster/setting line, proscenium, stage edges, wings, crossover, screen, curtains, doors, stairs, power, DMX, audio and network points. Add dimensions and photo IDs. Upload a photo of your sketch in the Photo Register (tag area 'Floor plan sketch')." },
      { type: "fields", key: "sketch_meta", fields: [
        { k: "scale", label: "Sketch scale / basis", type: "text" },
        { k: "datum", label: "Datum / reference used", type: "text" },
      ] },
      { type: "textarea", key: "sketch_notes", label: "Floor-plan notes" },
    ],
  },
  {
    id: "7", part: "B", num: "7", title: "Lighting positions — LX bars, FOH & side positions",
    blocks: [
      { type: "note", text: "Physically count every bar/truss. Do not assume production drawings are correct. Record SWL only where documented." },
      { type: "fields", key: "counts", title: "Actual positions found", fields: [
        { k: "lx_bars", label: "Usable stage LX bars found", type: "number" },
        { k: "foh", label: "FOH lighting positions found", type: "number" },
        { k: "side", label: "Side-lighting positions/trusses found", type: "number" },
      ] },
      { type: "table", key: "positions", title: "Lighting positions", addLabel: "Add position", columns: [
        { k: "id", label: "Position ID" },
        { k: "location", label: "Location" },
        { k: "length", label: "Length" },
        { k: "height", label: "Height/trim" },
        { k: "dist_pl", label: "Dist. from PL" },
        { k: "access", label: "Access/lowering" },
        { k: "photo", label: "Photo" },
      ] },
    ],
  },
  {
    id: "8", part: "B", num: "8", title: "Lighting fixture inventory — installed",
    blocks: [
      { type: "note", text: "Give every fixture a permanent venue ID (e.g. FOH-01, LX1-01) independent of any show." },
      { type: "table", key: "fixtures", title: "Installed fixtures", addLabel: "Add fixture", columns: [
        { k: "id", label: "ID" },
        { k: "position", label: "Position" },
        { k: "make", label: "Make" },
        { k: "model", label: "Model" },
        { k: "ftype", label: "Type" },
        { k: "mode", label: "Mode" },
        { k: "dmx", label: "DMX addr." },
        { k: "ch", label: "Ch." },
        { k: "status", label: "Status", type: "select", options: STATUS_OPTS },
      ] },
    ],
  },
  {
    id: "8A", part: "B", num: "8A", title: "Fixture inventory — detail & functional checks",
    blocks: [
      { type: "note", text: "Continue using the same IDs as section 8." },
      { type: "table", key: "fixture_detail", title: "Fixture detail", addLabel: "Add row", columns: [
        { k: "id", label: "ID" },
        { k: "serial", label: "Serial / asset" },
        { k: "power", label: "Power / connector" },
        { k: "clamp", label: "Clamp" },
        { k: "bond", label: "Safety bond", type: "select", options: YN },
        { k: "beam", label: "Beam / zoom" },
        { k: "fault", label: "Fault / condition" },
        { k: "photo", label: "Photo ID" },
      ] },
      { type: "checklist", key: "func_checks", title: "Fixture functional checks", items: [
        { k: "intensity", label: "Intensity 0 / 50 / 100%" },
        { k: "rgb", label: "RGB / colour mixing" },
        { k: "white", label: "White / amber / UV if fitted" },
        { k: "zoom", label: "Zoom / focus if fitted" },
        { k: "shutter", label: "Shutter / strobe, then return to safe value" },
        { k: "dimming", label: "Smooth dimming" },
        { k: "dmx", label: "DMX response" },
        { k: "power", label: "Power connection" },
        { k: "bond", label: "Safety bond / clamp condition" },
      ] },
    ],
  },
  {
    id: "9", part: "B", num: "9", title: "DMX patch & distribution",
    blocks: [
      { type: "note", text: "Trace the physical signal route as well as the addresses." },
      { type: "table", key: "patch", title: "DMX patch", addLabel: "Add patch row", columns: [
        { k: "device", label: "Fixture / device" },
        { k: "universe", label: "Universe" },
        { k: "start", label: "Start" },
        { k: "end", label: "End" },
        { k: "mode", label: "Mode" },
        { k: "channels", label: "Channels" },
        { k: "notes", label: "Notes" },
      ] },
      { type: "checklist", key: "dmx_infra", title: "DMX infrastructure", items: [
        { k: "outputs", label: "Console physical DMX output(s) identified" },
        { k: "sacn", label: "sACN / Art-Net use confirmed or ruled out" },
        { k: "outlets", label: "All DMX wall outlets identified" },
        { k: "splitters", label: "Splitters identified by make/model" },
        { k: "isolated", label: "Isolated outputs recorded" },
        { k: "connectors", label: "3-pin / 5-pin connector types recorded" },
        { k: "adapters", label: "Permanently-required adapters recorded" },
        { k: "route", label: "Cable route traced to FOH / LX1 / LX2 / SL / SR" },
        { k: "term", label: "Termination practice checked" },
        { k: "spare", label: "Spare DMX outputs recorded" },
        { k: "overlaps", label: "Address overlaps checked" },
        { k: "verified", label: "Fixture mode/address physically verified against menu" },
      ] },
    ],
  },
  // ---------------- PART C ----------------
  {
    id: "10", part: "C", num: "10", title: "LSC Mantra Lite — baseline audit & backup",
    blocks: [
      { type: "note", variant: "warning", text: "Do this BEFORE repatching, importing a new show, updating firmware or changing the fixture library." },
      { type: "fields", key: "console", title: "Console documentation", fields: [
        { k: "model", label: "Console model", type: "text" },
        { k: "serial", label: "Serial number", type: "text" },
        { k: "show_name", label: "Current show name", type: "text" },
        { k: "software", label: "Software / firmware version", type: "text" },
        { k: "cpu", label: "CPU (About screen)", type: "text" },
        { k: "lib", label: "Fixture library version", type: "text" },
        { k: "wing1", label: "Wing 1 detected / details", type: "text" },
        { k: "wing2", label: "Wing 2 detected / details", type: "text" },
        { k: "dmx_cfg", label: "DMX output / universe config", type: "text" },
        { k: "ip", label: "IP / network settings", type: "text" },
      ] },
      { type: "checklist", key: "document_state", title: "Document the current state", items: [
        { k: "console", label: "Photograph console and both Wings" },
        { k: "rear", label: "Photograph rear connections" },
        { k: "about", label: "Photograph About screen" },
        { k: "rigview", label: "Photograph current Rig View" },
        { k: "patch", label: "Photograph current Patch screens" },
        { k: "memory", label: "Photograph memory/cue-list pages" },
        { k: "startup", label: "Record Default Show / startup behaviour" },
      ] },
      { type: "checklist", key: "backup_order", title: "Backup — in this order", items: [
        { k: "s1", label: "1. Insert USB A and export the existing show" },
        { k: "s2", label: "2. Verify the exported file exists and can be read" },
        { k: "s3", label: "3. Copy the exported show to the laptop" },
        { k: "s4", label: "4. Copy to USB B / a second location" },
        { k: "s5", label: "5. Record backup filenames below" },
        { k: "s6", label: "6. Do not overwrite the only existing copy" },
      ] },
      { type: "fields", key: "backup_files", title: "Backup filenames", fields: [
        { k: "usb_a", label: "USB A backup filename", type: "text" },
        { k: "laptop", label: "Laptop copy path / filename", type: "text" },
        { k: "usb_b", label: "USB B backup filename", type: "text" },
      ] },
    ],
  },
  {
    id: "11", part: "C", num: "11", title: "Mantra firmware, software & fixture-library decision",
    blocks: [
      { type: "note", variant: "warning", text: "CPU Model 2 → DO NOT install v4.x (legacy v3.07-or-earlier package applies). CPU Model 3+ → v4.x compatible only if every decision item is ticked." },
      { type: "fields", key: "context", title: "Context", fields: [
        { k: "lsc_checked", label: "Official LSC listing checked (date)", type: "date" },
        { k: "sw_version", label: "Software version available", type: "text" },
        { k: "lib_version", label: "Fixture library version available", type: "text" },
      ] },
      { type: "checklist", key: "decision", title: "Decision record", items: [
        { k: "exported", label: "Existing venue show exported and verified in ≥ 2 places" },
        { k: "about", label: "About screen photographed" },
        { k: "sw_recorded", label: "Current software version recorded" },
        { k: "cpu_recorded", label: "CPU model recorded" },
        { k: "release", label: "Current official LSC release confirmed on the day" },
        { k: "firmware", label: "Correct firmware file downloaded from official LSC source" },
        { k: "lib", label: "Correct fixture-library package for console generation" },
        { k: "power", label: "Stable mains power available" },
        { k: "no_show", label: "No performance/rehearsal about to begin" },
        { k: "authorised", label: "Venue representative authorises update" },
      ] },
      { type: "fields", key: "result", title: "Result", fields: [
        { k: "cpu_result", label: "CPU result", type: "text" },
        { k: "permitted", label: "Update permitted?", type: "select", options: ["YES", "NO"] },
        { k: "reason", label: "Reason / compatibility note", type: "textarea", full: true },
        { k: "firmware_file", label: "Firmware file used", type: "text" },
        { k: "lib_file", label: "Fixture library file used", type: "text" },
      ] },
    ],
  },
  {
    id: "12", part: "C", num: "12", title: "Post-update validation & permanent hall baseline",
    blocks: [
      { type: "note", text: "The permanent hall file should describe the installed venue, not a particular production." },
      { type: "checklist", key: "update_steps", title: "Update steps", items: [
        { k: "s1", label: "1. Prepare update USB with LSC folder + official update file" },
        { k: "s2", label: "2. Insert update USB only after backups complete" },
        { k: "s3", label: "3. Home > Tools > Setup > System Settings > Update Firmware" },
        { k: "s4", label: "4. Follow console prompts, allow process to finish" },
        { k: "s5", label: "5. Restart/reset exactly as required by LSC" },
        { k: "s6", label: "6. Re-open About and photograph new version" },
        { k: "s7", label: "7. Confirm fixture library version" },
        { k: "s8", label: "8. Reload/verify venue baseline show" },
        { k: "s9", label: "9. Check both Wings" },
        { k: "s10", label: "10. Check DMX output" },
        { k: "s11", label: "11. Check patch and Rig View" },
        { k: "s12", label: "12. Test every fixture and attribute" },
        { k: "s13", label: "13. Re-export a new post-update baseline backup" },
      ] },
      { type: "checklist", key: "validation", title: "Post-update validation", items: [
        { k: "boots", label: "Console boots normally" },
        { k: "controls", label: "Touchscreen / faders / buttons operate" },
        { k: "wing1", label: "Wing 1 recognised" },
        { k: "wing2", label: "Wing 2 recognised" },
        { k: "dmx", label: "DMX output operates" },
        { k: "net", label: "Network settings retained / corrected" },
        { k: "patch", label: "Patch survived or rebuilt from verified inventory" },
        { k: "rigview", label: "Rig View matches real physical layout" },
        { k: "fixtures", label: "Each fixture responds individually" },
        { k: "levels", label: "0 / 50 / 100% tests complete" },
        { k: "attrs", label: "All colour / beam attributes checked" },
        { k: "overlaps", label: "No address overlaps" },
        { k: "ids", label: "Permanent fixture IDs used consistently" },
        { k: "memories", label: "Useful venue test memories created" },
        { k: "baseline", label: "Venue BASELINE file saved" },
        { k: "exported", label: "BASELINE exported to USB and laptop" },
        { k: "retained", label: "Old pre-update backup retained unchanged" },
      ] },
      { type: "fields", key: "baseline", title: "Baseline record", fields: [
        { k: "filename", label: "Baseline filename", type: "text", placeholder: "Plantagenet_Hall_BASELINE_" },
        { k: "rev", label: "Baseline revision / date", type: "text" },
        { k: "storage", label: "Where master backup is stored", type: "text" },
      ] },
      { type: "textarea", key: "notes", label: "Mantra notes / changes made" },
    ],
  },
  // ---------------- PART D ----------------
  {
    id: "13", part: "D", num: "13", title: "Technical power survey",
    blocks: [
      { type: "note", text: "Record labels and documented ratings. Do not infer circuit capacity or compliance from appearance." },
      { type: "checklist", key: "power_check", title: "Power checklist", items: [
        { k: "schedule", label: "Switchboard/circuit schedule photographed (with permission)" },
        { k: "tech", label: "Technical power circuits identified" },
        { k: "foh", label: "FOH control power identified" },
        { k: "sides", label: "Stage Left / Stage Right power identified" },
        { k: "lx", label: "LX bar/truss power feeds identified" },
        { k: "projector", label: "Projector power identified" },
        { k: "audio", label: "Audio rack/console power identified" },
        { k: "shared", label: "Known shared loads recorded" },
        { k: "rcd", label: "RCD protection recorded where documented" },
        { k: "tag", label: "Test-and-tag condition noted where visible" },
      ] },
      { type: "table", key: "outlets", title: "Power outlets", addLabel: "Add outlet", columns: [
        { k: "id", label: "Outlet ID" },
        { k: "location", label: "Location" },
        { k: "connector", label: "Connector" },
        { k: "circuit", label: "Circuit label" },
        { k: "rating", label: "Breaker / rating" },
        { k: "rcd", label: "RCD?", type: "select", options: YN },
        { k: "notes", label: "Normal use / notes" },
      ] },
    ],
  },
  {
    id: "14", part: "D", num: "14", title: "Audio system — console, stage inputs & processing",
    blocks: [
      { type: "fields", key: "console", title: "Console", fields: [
        { k: "make", label: "Console make / exact model", type: "text" },
        { k: "firmware", label: "Firmware version", type: "text" },
        { k: "scene", label: "Current scene / show file", type: "text" },
        { k: "serial", label: "Serial / asset ID", type: "text" },
        { k: "stagebox", label: "Stagebox / digital snake model", type: "text" },
      ] },
      { type: "checklist", key: "doc", title: "Documentation & network", items: [
        { k: "front", label: "Photograph console front and I/O" },
        { k: "stagebox", label: "Photograph stagebox and patch panels" },
        { k: "dsp", label: "Photograph DSP / EQ / amplifiers" },
        { k: "backup", label: "Export/backup current scene/show if authorised" },
        { k: "trace_in", label: "Trace stage input to console" },
        { k: "trace_out", label: "Trace console output → processing → amps → speakers" },
        { k: "qlab", label: "Record QLab/playback return path" },
        { k: "video", label: "Record projector/video audio path if used" },
        { k: "wired", label: "Identify wired microphones" },
        { k: "wireless", label: "Identify wireless receiver/transmitter systems" },
        { k: "freq", label: "Record wireless frequency band / coordination" },
        { k: "comms", label: "Check talkback/comms integration if present" },
      ] },
      { type: "table", key: "inputs", title: "Input patch", addLabel: "Add input", columns: [
        { k: "input", label: "Input / socket" },
        { k: "location", label: "Physical location" },
        { k: "channel", label: "Console ch." },
        { k: "use", label: "Typical venue use" },
        { k: "notes", label: "Test / notes" },
      ] },
    ],
  },
  {
    id: "15", part: "D", num: "15", title: "Audio system — loudspeakers & output testing",
    blocks: [
      { type: "table", key: "speakers", title: "Loudspeakers", addLabel: "Add speaker", columns: [
        { k: "id", label: "ID" },
        { k: "location", label: "Location" },
        { k: "make", label: "Make" },
        { k: "model", label: "Model" },
        { k: "powered", label: "Powered?", type: "select", options: YN },
        { k: "amp", label: "Amp / output" },
        { k: "aim", label: "Height / aim" },
        { k: "status", label: "Status", type: "select", options: STATUS_OPTS },
      ] },
      { type: "checklist", key: "testing", title: "Output testing", items: [
        { k: "ml", label: "Main Left tested" },
        { k: "mr", label: "Main Right tested" },
        { k: "centre", label: "Centre tested if fitted" },
        { k: "fills", label: "Front fills tested if fitted" },
        { k: "delay", label: "Delay speakers tested if fitted" },
        { k: "subs", label: "Subs tested if fitted" },
        { k: "monitors", label: "Stage monitors tested" },
        { k: "walk", label: "Walk entire audience area with reference material" },
        { k: "hum", label: "Check hum / buzz / noise" },
        { k: "drivers", label: "Check for damaged drivers / distortion" },
        { k: "biobox", label: "Record operator listening limitations from bio box" },
      ] },
      { type: "textarea", key: "notes", label: "Audio test notes" },
    ],
  },
  {
    id: "16", part: "D", num: "16", title: "Projection screen & projector measurements",
    blocks: [
      { type: "note", text: "Measure the actual usable image area — not only the outer screen frame/casing." },
      { type: "measurements", key: "proj_dims", rows: [
        { k: "screen_model", label: "Screen manufacturer / model", unit: "" },
        { k: "fabric_w", label: "Screen fabric width", unit: "mm" },
        { k: "fabric_h", label: "Screen fabric height", unit: "mm" },
        { k: "image_w", label: "Usable projected image width", unit: "mm" },
        { k: "image_h", label: "Usable projected image height", unit: "mm" },
        { k: "screen_floor", label: "Screen bottom above stage floor", unit: "mm" },
        { k: "throw", label: "Throw distance (lens to screen)", unit: "mm" },
        { k: "offset_h", label: "Horizontal offset from CL", unit: "mm" },
        { k: "offset_v", label: "Vertical offset", unit: "mm" },
        { k: "proj_model", label: "Projector make / exact model", unit: "" },
      ] },
      { type: "textarea", key: "notes", label: "Projection notes" },
    ],
  },
  {
    id: "17", part: "D", num: "17", title: "Projection functional test & signal path",
    blocks: [
      { type: "checklist", key: "document", title: "Document", items: [
        { k: "front", label: "Photograph projector front, rear I/O, model plate, lens, mount, bond" },
        { k: "screen", label: "Photograph screen straight-on" },
        { k: "throw", label: "Photograph from projector position to screen" },
        { k: "trace", label: "Trace video signal from bio box/playback to projector" },
        { k: "extenders", label: "Record extenders/converters/switchers and PSUs" },
        { k: "menu", label: "Photograph important menu/settings pages" },
        { k: "shutdown", label: "Confirm shutdown/cool-down procedure" },
      ] },
      { type: "checklist", key: "test", title: "Test", items: [
        { k: "cold", label: "Power from cold; note warnings" },
        { k: "white", label: "Full white field" },
        { k: "black", label: "Black field" },
        { k: "bars", label: "Colour bars / test image" },
        { k: "grid", label: "Grid / geometry pattern" },
        { k: "coverage", label: "Screen coverage" },
        { k: "focus", label: "Centre focus + all four corners" },
        { k: "keystone", label: "Geometry / keystone" },
        { k: "lens", label: "Record lens shift / zoom state" },
        { k: "uniform", label: "Brightness + colour uniformity" },
        { k: "stability", label: "Image stability" },
        { k: "pixels", label: "Dead pixels / dust blobs" },
        { k: "qlab", label: "QLab or test computer → projector" },
        { k: "audio", label: "Audio with video if applicable" },
      ] },
      { type: "fields", key: "signal", title: "Signal path", fields: [
        { k: "path", label: "Current signal path", type: "textarea", full: true },
        { k: "adapters", label: "Known adapters / extenders", type: "text", full: true },
      ] },
      { type: "textarea", key: "faults", label: "Projector faults / observations" },
    ],
  },
  {
    id: "18", part: "D", num: "18", title: "Bio box / technical control booth",
    blocks: [
      { type: "measurements", key: "booth_dims", rows: [
        { k: "width", label: "Internal width", unit: "mm" },
        { k: "depth", label: "Internal depth", unit: "mm" },
        { k: "height", label: "Internal height", unit: "mm" },
        { k: "bench_w", label: "Bench width", unit: "mm" },
        { k: "bench_d", label: "Bench depth", unit: "mm" },
        { k: "door_w", label: "Door clear opening W", unit: "mm" },
        { k: "door_h", label: "Door clear opening H", unit: "mm" },
        { k: "window_w", label: "Viewing window width", unit: "mm" },
        { k: "window_h", label: "Viewing window height", unit: "mm" },
        { k: "sill_h", label: "Viewing window sill height", unit: "mm" },
      ] },
      { type: "checklist", key: "booth", title: "Booth checklist", items: [
        { k: "angles", label: "Booth photographed from several angles" },
        { k: "mantra", label: "Mantra location recorded" },
        { k: "wings", label: "Both Wings location recorded" },
        { k: "sl", label: "StudioLive location recorded" },
        { k: "qlab", label: "QLab/computer positions recorded" },
        { k: "monitors", label: "Monitor/Stream Deck positions recorded" },
        { k: "dmx", label: "DMX patch/outlet locations recorded" },
        { k: "audio", label: "Audio patch locations recorded" },
        { k: "net", label: "Network switch/router/outlets recorded" },
        { k: "power", label: "Power outlets recorded" },
        { k: "comms", label: "Comms position recorded" },
        { k: "proj", label: "Projector controls recorded" },
        { k: "storage", label: "Storage recorded" },
      ] },
      { type: "fields", key: "sightlines", title: "Sightlines & monitoring", fields: [
        { k: "see_stage", label: "Lighting op can see entire stage?", type: "select", options: YN },
        { k: "hear_aud", label: "Sound op can adequately hear auditorium?", type: "select", options: YN },
        { k: "window_effect", label: "Booth window open vs closed affects monitoring?", type: "select", options: YN },
      ] },
    ],
  },
  {
    id: "19", part: "D", num: "19", title: "Network & show-control infrastructure",
    blocks: [
      { type: "note", variant: "warning", text: "Document the venue network without recording passwords in the widely circulated technical plan." },
      { type: "checklist", key: "net_check", title: "Network checklist", items: [
        { k: "router", label: "Router identified" },
        { k: "switch", label: "Network switch(es) identified" },
        { k: "ap", label: "Access point identified" },
        { k: "mantra", label: "Mantra Ethernet settings recorded" },
        { k: "sl", label: "StudioLive network settings recorded" },
        { k: "proj", label: "Projector network settings recorded if used" },
        { k: "biobox", label: "Bio box data outlets identified" },
        { k: "stage", label: "Stage data outlets identified" },
        { k: "qlab", label: "QLab test computer connected" },
        { k: "sacn", label: "sACN / Art-Net presence checked" },
        { k: "osc", label: "OSC / remote-trigger capability noted" },
        { k: "dhcp", label: "Existing DHCP / static scheme recorded" },
        { k: "pw", label: "Passwords excluded from general-reference copy" },
        { k: "restored", label: "Temporary test changes restored or documented" },
      ] },
      { type: "table", key: "devices", title: "Network devices", addLabel: "Add device", columns: [
        { k: "device", label: "Device" },
        { k: "location", label: "Location" },
        { k: "model", label: "Make / model" },
        { k: "connection", label: "Connection" },
        { k: "ip", label: "IP / DHCP note" },
        { k: "port", label: "Port / notes" },
      ] },
    ],
  },
  {
    id: "20", part: "D", num: "20", title: "Curtains, masking, rigging & overhead infrastructure",
    blocks: [
      { type: "note", variant: "warning", text: "Documented SWL / inspection info copied only where authorised. No estimated load rating entered as fact." },
      { type: "checklist", key: "rig_check", title: "Overhead checklist", items: [
        { k: "main", label: "Main curtain / house tabs" },
        { k: "legs", label: "Legs" },
        { k: "borders", label: "Borders" },
        { k: "blacks", label: "Upstage blacks / travellers" },
        { k: "cyc", label: "Screen / cyc" },
        { k: "tracks", label: "Tracks and operating method" },
        { k: "foh", label: "FOH bar(s)" },
        { k: "lx", label: "Stage LX bar(s)" },
        { k: "side", label: "Side trusses" },
        { k: "proj", label: "Projector mount / bar" },
        { k: "mirror", label: "Mirror-ball motor / support" },
        { k: "hoists", label: "Any hoists / lowering systems" },
        { k: "access", label: "Access method to overhead positions" },
        { k: "swl", label: "Documented SWL / inspection info (authorised only)" },
      ] },
      { type: "table", key: "rigging", title: "Rigging positions", addLabel: "Add position", columns: [
        { k: "id", label: "ID" },
        { k: "item", label: "Item / position" },
        { k: "width", label: "Width / length" },
        { k: "drop", label: "Drop / height" },
        { k: "operation", label: "Operation" },
        { k: "rating", label: "Documented rating" },
        { k: "photo", label: "Photo / notes" },
      ] },
    ],
  },
  {
    id: "21", part: "D", num: "21", title: "Effects, comms, house/work lights & backstage",
    blocks: [
      { type: "checklist", key: "effects", title: "Haze / mirror ball / effects", items: [
        { k: "haze_model", label: "Haze machine make/model/serial recorded" },
        { k: "haze_dmx", label: "Haze DMX/manual control recorded" },
        { k: "haze_loc", label: "Approved haze operating location recorded" },
        { k: "haze_proc", label: "Venue haze / fire-alarm procedure obtained" },
        { k: "mirror_dia", label: "Mirror ball diameter/location recorded" },
        { k: "mirror_motor", label: "Mirror ball motor make/model recorded" },
        { k: "secondary", label: "Secondary safety checked/documented" },
        { k: "pinspots", label: "Pinspots identified and tested" },
        { k: "other", label: "Other practical effects infrastructure recorded" },
      ] },
      { type: "checklist", key: "house", title: "House / work lights", items: [
        { k: "house", label: "House-light controls located and tested" },
        { k: "work", label: "Stage work-light controls located and tested" },
        { k: "backstage", label: "Backstage work lights checked" },
        { k: "emergency", label: "Emergency-lighting controls left untouched except as authorised" },
        { k: "mantra", label: "Any Mantra-controlled house/work circuits documented" },
        { k: "fallback", label: "Independent manual fallback documented" },
      ] },
      { type: "checklist", key: "comms", title: "Comms / show call", items: [
        { k: "wired", label: "Wired headset points mapped" },
        { k: "wireless", label: "Wireless comms equipment inventoried" },
        { k: "sm", label: "Stage Manager position recorded" },
        { k: "foh", label: "FOH communication method recorded" },
        { k: "page", label: "Dressing-room/page system recorded" },
        { k: "cue", label: "Cue lights recorded" },
        { k: "program", label: "Program audio / backstage monitor recorded" },
        { k: "hearing", label: "Hearing assistance system noted if present" },
      ] },
      { type: "textarea", key: "notes", label: "Effects / comms / house-light notes" },
    ],
  },
  {
    id: "22", part: "D", num: "22", title: "Spare equipment, storage, loading & access",
    blocks: [
      { type: "checklist", key: "spares", title: "Spare / stored equipment", items: [
        { k: "fixtures", label: "Spare lighting fixtures" },
        { k: "clamps", label: "Spare clamps" },
        { k: "bonds", label: "Safety bonds" },
        { k: "dmx", label: "DMX leads / adapters" },
        { k: "xlr", label: "XLR / audio leads" },
        { k: "eth", label: "Ethernet leads" },
        { k: "power", label: "Power leads / approved distribution" },
        { k: "hdmi", label: "HDMI / video leads / extenders" },
        { k: "mics", label: "Microphones" },
        { k: "di", label: "DI boxes" },
        { k: "mic_stands", label: "Mic stands" },
        { k: "spk_stands", label: "Speaker stands" },
        { k: "remotes", label: "Remote controls" },
        { k: "haze_fluid", label: "Haze fluid" },
        { k: "lamps", label: "Lamps / consumables" },
        { k: "tools", label: "Tools belonging to venue" },
        { k: "unknown", label: "Unidentified equipment photographed" },
      ] },
      { type: "checklist", key: "access", title: "Access", items: [
        { k: "loading", label: "Loading entrance photographed" },
        { k: "door_dims", label: "Loading door dimensions recorded" },
        { k: "route", label: "Route loading area → stage measured" },
        { k: "steps", label: "Steps / ramps recorded" },
        { k: "clearances", label: "Largest equipment clearances recorded" },
        { k: "rooms", label: "Dressing rooms / green room located" },
        { k: "storage", label: "Technical storage locations mapped" },
        { k: "ladder", label: "Ladder/EWP access arrangement recorded" },
        { k: "biobox", label: "Bio box access recorded" },
        { k: "sides", label: "Stage Left/Right access recorded" },
        { k: "keys", label: "Keys / access restrictions noted (no sensitive info published)" },
      ] },
      { type: "textarea", key: "notes", label: "Storage / access notes" },
    ],
  },
  // ---------------- PART E ----------------
  {
    id: "23", part: "E", num: "23", title: "Complete end-to-end system test",
    blocks: [
      { type: "note", text: "Run this after documentation and any authorised firmware/patch work is complete." },
      { type: "checklist", key: "lighting", title: "Lighting", items: [
        { k: "boots", label: "Mantra boots and loads intended baseline" },
        { k: "wing1", label: "Wing 1 operates" },
        { k: "wing2", label: "Wing 2 operates" },
        { k: "fixtures", label: "All permanent fixtures respond individually" },
        { k: "foh", label: "FOH group test" },
        { k: "lx", label: "Each LX bar / group test" },
        { k: "white", label: "Full-stage neutral white test" },
        { k: "mirror", label: "Mirror-ball / pinspot test" },
        { k: "haze", label: "Haze control test if permitted" },
        { k: "house", label: "House/work light relationship checked" },
        { k: "blackout", label: "Blackout / safe recovery state checked" },
      ] },
      { type: "checklist", key: "audio", title: "Audio", items: [
        { k: "input", label: "Stage input test" },
        { k: "wired", label: "Wired mic test" },
        { k: "wireless", label: "Wireless system test" },
        { k: "playback", label: "QLab/playback stereo test" },
        { k: "ml", label: "Main L" },
        { k: "mr", label: "Main R" },
        { k: "fills", label: "Fills / delays" },
        { k: "subs", label: "Subs" },
        { k: "monitors", label: "Monitors" },
        { k: "walk", label: "Walk-test audience area" },
        { k: "noise", label: "No abnormal hum / buzz / distortion" },
      ] },
      { type: "checklist", key: "projection", title: "Projection / control", items: [
        { k: "playback", label: "Playback computer → projector" },
        { k: "fullscreen", label: "Full-screen image" },
        { k: "geometry", label: "Correct resolution / geometry" },
        { k: "av", label: "Video + audio together" },
        { k: "blackout", label: "Projector blackout / blanking" },
        { k: "net", label: "Network connectivity where used" },
        { k: "remote", label: "Mantra remote/show-control test if required" },
        { k: "fallback", label: "Manual fallback confirmed" },
      ] },
      { type: "textarea", key: "notes", label: "System test notes" },
    ],
  },
  {
    id: "24", part: "E", num: "24", title: "Fault, maintenance & improvement register",
    blocks: [
      { type: "note", text: "Status: Operational / Operational with issue / Requires maintenance / Do not use. Only mark 'Do not use' where genuinely warranted." },
      { type: "table", key: "faults", title: "Fault register", addLabel: "Add fault", columns: [
        { k: "id", label: "ID" },
        { k: "area", label: "Area / equipment" },
        { k: "fault", label: "Fault / observation" },
        { k: "status", label: "Status", type: "select", options: ["Operational", "Operational with issue", "Requires maintenance", "Do not use"] },
        { k: "priority", label: "Priority", type: "select", options: ["Critical", "High", "Medium", "Low"] },
        { k: "action", label: "Action / owner" },
        { k: "photo", label: "Photo" },
      ] },
    ],
  },
  {
    id: "25", part: "E", num: "25", title: "Data check for the permanent venue plan",
    blocks: [
      { type: "note", text: "Use this before leaving to make sure the as-built package can be completed without another visit." },
      { type: "checklist", key: "stage_plan", title: "Stage plan", items: [
        { k: "cl", label: "Centre line and plaster/setting line established" },
        { k: "dims", label: "All stage dimensions complete" },
        { k: "wings", label: "Wings/crossover complete" },
        { k: "doors", label: "Doors/stairs/access shown" },
        { k: "curtains", label: "Curtains/screen positions measured" },
      ] },
      { type: "checklist", key: "control", title: "Control", items: [
        { k: "mantra", label: "Mantra model/serial/version/CPU recorded" },
        { k: "backup", label: "Existing show safely backed up" },
        { k: "post", label: "Post-update state recorded if changed" },
        { k: "baseline", label: "Baseline venue file exported" },
        { k: "biobox", label: "Bio box layout measured" },
      ] },
      { type: "checklist", key: "projection", title: "Projection", items: [
        { k: "image", label: "Screen usable image measured" },
        { k: "model", label: "Projector exact model/lens recorded" },
        { k: "throw", label: "Throw and offsets measured" },
        { k: "chain", label: "Signal chain traced" },
        { k: "tested", label: "Projector tested" },
      ] },
      { type: "checklist", key: "lighting", title: "Lighting", items: [
        { k: "positions", label: "All FOH/LX/side positions confirmed" },
        { k: "ids", label: "Every fixture has a permanent ID" },
        { k: "models", label: "Exact make/model/mode recorded" },
        { k: "dmx", label: "DMX address/footprint verified" },
        { k: "routes", label: "Power and DMX routes traced" },
      ] },
      { type: "checklist", key: "audio", title: "Audio", items: [
        { k: "console", label: "Exact console model/firmware recorded" },
        { k: "io", label: "Inputs/outputs traced" },
        { k: "racks", label: "Stagebox/racks documented" },
        { k: "speakers", label: "Every speaker located/identified" },
        { k: "wireless", label: "Wireless systems/bands documented" },
      ] },
      { type: "checklist", key: "infra", title: "Infrastructure", items: [
        { k: "power", label: "Power outlets/circuit labels recorded" },
        { k: "net", label: "Network devices/topology recorded" },
        { k: "comms", label: "Comms mapped" },
        { k: "house", label: "House/work lights mapped" },
        { k: "haze", label: "Haze/effects procedures recorded" },
        { k: "access", label: "Access/loading/storage captured" },
      ] },
      { type: "checklist", key: "documentation", title: "Documentation", items: [
        { k: "photo_reg", label: "Photo register complete" },
        { k: "fault_reg", label: "Fault register complete" },
        { k: "legible", label: "All handwritten pages legible" },
        { k: "backed_up", label: "Digital photos backed up" },
        { k: "console_backup", label: "Console backups copied to two locations" },
      ] },
    ],
  },
  {
    id: "26", part: "E", num: "26", title: "Before leaving the venue — final sign-off",
    blocks: [
      { type: "checklist", key: "signoff", title: "Final sign-off checklist", items: [
        { k: "photographed", label: "Stage and auditorium fully photographed" },
        { k: "measured", label: "Stage/proscenium/wings/auditorium measured" },
        { k: "screen", label: "Projection screen measured" },
        { k: "projector", label: "Projector model/lens/throw confirmed" },
        { k: "positions", label: "Actual LX/FOH/side positions counted and measured" },
        { k: "fixtures", label: "All lighting fixtures inventoried and tested" },
        { k: "dmx", label: "Current DMX patch recorded" },
        { k: "show", label: "Existing Mantra show exported and verified" },
        { k: "firmware", label: "Mantra firmware/CPU/library recorded" },
        { k: "update", label: "Any authorised firmware update fully validated" },
        { k: "sl", label: "StudioLive exact model/firmware recorded" },
        { k: "audio", label: "Audio patch and speakers documented/tested" },
        { k: "power", label: "Power/data/network recorded" },
        { k: "biobox", label: "Bio box measured and photographed" },
        { k: "curtains", label: "Curtains/masking/overhead positions recorded" },
        { k: "haze", label: "Haze/comms/house/work lights documented" },
        { k: "storage", label: "Storage/spares/access documented" },
        { k: "faults", label: "Fault/action register completed" },
        { k: "leads", label: "All temporary test leads/adapters removed or restored" },
        { k: "settings", label: "Settings changed during testing restored or documented" },
        { k: "backed", label: "Photos copied to laptop/cloud/second location" },
        { k: "backups", label: "Console/audio/projector backups labelled and stored" },
        { k: "advised", label: "Venue representative advised of any faults or changes" },
      ] },
      { type: "fields", key: "signatures", title: "Sign-off", fields: [
        { k: "completed_by", label: "Survey completed by (name)", type: "text" },
        { k: "venue_rep", label: "Venue representative (name)", type: "text" },
        { k: "datetime", label: "Date / time completed", type: "text" },
      ] },
      { type: "textarea", key: "changes", label: "Changes made during visit" },
      { type: "textarea", key: "outstanding", label: "Outstanding items / return visit needed" },
    ],
  },
  // ---------------- PART F ----------------
  {
    id: "27", part: "F", num: "27", title: "Permanent hall technical-reference package",
    blocks: [
      { type: "note", text: "Recommended show-independent master document structure. Keep the venue reference stable; keep show-specific cueing, focus, colour looks and patches in separate production documents." },
      { type: "checklist", key: "package", title: "Documents to include", items: [
        { k: "d01", label: "01 Venue overview & technical contacts" },
        { k: "d02", label: "02 Stage specifications" },
        { k: "d03", label: "03 Dimensioned stage floor plan" },
        { k: "d04", label: "04 Stage longitudinal section/elevation" },
        { k: "d05", label: "05 Auditorium plan" },
        { k: "d06", label: "06 Lighting-position plan" },
        { k: "d07", label: "07 Permanent fixture inventory" },
        { k: "d08", label: "08 Permanent DMX patch" },
        { k: "d09", label: "09 DMX topology/distribution diagram" },
        { k: "d10", label: "10 Mantra Lite configuration & baseline file record" },
        { k: "d11", label: "11 Power distribution reference" },
        { k: "d12", label: "12 Audio equipment inventory" },
        { k: "d13", label: "13 Audio signal-flow diagram" },
        { k: "d14", label: "14 Audio input/output patch" },
        { k: "d15", label: "15 Speaker plan" },
        { k: "d16", label: "16 Projection system & geometry" },
        { k: "d17", label: "17 Bio box/control-room plan" },
        { k: "d18", label: "18 Technical network diagram" },
        { k: "d19", label: "19 Curtains/masking plan" },
        { k: "d20", label: "20 Rigging positions & documented ratings" },
        { k: "d21", label: "21 Communications / show-call systems" },
        { k: "d22", label: "22 Haze/effects infrastructure" },
        { k: "d23", label: "23 Backstage/loading/access" },
        { k: "d24", label: "24 Spare equipment inventory" },
        { k: "d25", label: "25 Startup / shutdown / basic test procedures" },
        { k: "d26", label: "26 Maintenance/fault register" },
        { k: "d27", label: "27 Full photographic reference" },
        { k: "d28", label: "28 Revision/change register" },
      ] },
    ],
  },
  {
    id: "28", part: "F", num: "28", title: "Source & verification notes",
    blocks: [
      { type: "note", variant: "warning", text: "Installed venue condition takes precedence. Always re-check the official LSC product/download page immediately before performing an update." },
      { type: "textarea", key: "docs_reviewed", label: "Project documents reviewed" },
      { type: "textarea", key: "planning_status", label: "Current planning status" },
      { type: "textarea", key: "lsc_info", label: "Official LSC info (checked [date])" },
      { type: "textarea", key: "additional", label: "Additional venue notes & follow-up" },
    ],
  },
];

export const getSection = (id) => SECTIONS.find((s) => s.id === id);
